import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import {
  LoadingStateComponent,
  useChatContext,
  useLabraChat,
  useTrackEvent,
} from '@labrav/common'
import { FundingDataProps } from '../../types'
import {
  FundingRunDetailsResponse,
  getFundingRequestDetails,
} from '../../api/fundingApi'
import { BannerActionKey, StatusBanner } from './StatusBanner'
import { ProgramsFundingDetails } from './ProgramsFundingDetails'
import { ContactInformation } from './ContactInformation'
import { SupportingDocuments } from './SupportingDocuments'
import { FundRequestProgress } from './FundRequestProgress'
import { RecentActivities } from './RecentActivities'
import { FundingBreadcrumb } from './FundingBreadcrumb'
import {
  buildContactPdmPrompt,
  buildReviewPrompt,
  buildWithdrawPrompt,
} from './constants'
import { useStyles } from './FundingRequestDetail.styles'

/** What each banner action reports itself as. */
const ACTION_COMPONENTS: Record<BannerActionKey, string> = {
  contactPdm: 'contact_pdm',
  withdraw: 'withdraw_request',
  resend: 'resend_request',
}

interface FundingRequestDetailProps extends FundingDataProps {
  onHeaderChange?: (header: ReactNode | null) => void
}

export const FundingRequestDetail = ({
  accountId,
  serviceUrl,
  onHeaderChange,
}: FundingRequestDetailProps) => {
  const { classes } = useStyles()
  const { program, workflowId } = useParams<{
    program: string
    workflowId: string
  }>()
  const { open, sendPrompt } = useLabraChat()
  const track = useTrackEvent()
  const navigate = useNavigate()

  const [details, setDetails] = useState<FundingRunDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // Name this run the way the funding API does, so the agent can look it up
  // instead of guessing. `get_run` is keyed by program *and* an id, so both go —
  // sending the workflow id alone is what previously made the agent answer
  // "let me try checking if this is a POC funding run instead".
  useChatContext('fundingRequestDetail', () => ({
    program_name: program,
    workflow_id: workflowId,
    funding_id: details?.run?.funding_application_id,
    fund_request_id: details?.run?.fund_request_id,
    company_name: details?.run?.company_name,
    display_status: details?.run?.display_status,
  }))

  useEffect(() => {
    if (!serviceUrl || !accountId || !program || !workflowId) {
      setLoading(false)
      setError(true)
      return
    }
    let ignore = false
    setLoading(true)
    setError(false)
    ;(async () => {
      try {
        const { data } = await getFundingRequestDetails(
          serviceUrl,
          accountId,
          program,
          workflowId
        )
        if (!ignore) setDetails(data)
      } catch {
        if (!ignore) setError(true)
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, program, workflowId])

  const run = details?.run
  const events = details?.timeline?.events ?? []
  const breadcrumbLabel =
    run?.company_name || run?.fund_request_id || 'Funding request'

  useEffect(() => {
    if (!onHeaderChange || !run) return
    onHeaderChange(<FundingBreadcrumb current={breadcrumbLabel} />)
    return () => onHeaderChange(null)
  }, [onHeaderChange, run, breadcrumbLabel])

  const onReview = useCallback(() => {
    if (!run) return
    track({ component: 'review_with_ai', action: 'clicked' })
    open()
    sendPrompt(buildReviewPrompt(run.fund_request_id ?? run.workflow_id), {
      showUserMessage: true,
    })
  }, [open, sendPrompt, run, track])

  const onDocumentUploaded = useCallback(async () => {
    if (!serviceUrl || !accountId || !program || !workflowId) return
    const { data } = await getFundingRequestDetails(
      serviceUrl,
      accountId,
      program,
      workflowId
    )
    setDetails(data)
  }, [serviceUrl, accountId, program, workflowId])

  const onAction = useCallback(
    (key: BannerActionKey) => {
      if (!run) return
      // The agent works off the funding application id; the run ids identify the
      // same request when AWS has not issued one yet.
      const id =
        run.funding_application_id ?? run.fund_request_id ?? run.workflow_id
      const prompt =
        key === 'withdraw'
          ? buildWithdrawPrompt(id)
          : key === 'contactPdm'
            ? buildContactPdmPrompt(id)
            : null
      if (!prompt) return
      track({ component: ACTION_COMPONENTS[key], action: 'clicked' })
      open()
      sendPrompt(prompt, { showUserMessage: true })
    },
    [open, sendPrompt, run, track]
  )

  if (loading) {
    return (
      <div className={classes.stateWrap}>
        <LoadingStateComponent
          heading="Fetching this funding request"
          description="It may take a few moments, please wait..."
        />
      </div>
    )
  }

  if (error || !run) {
    return (
      <div className={classes.stateWrap}>
        <Typography component="p" className={classes.errorText}>
          We couldn&apos;t load this funding request.
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.root} data-testid="funding-request-detail">
      <div className={classes.columns}>
        <div className={classes.leftColumn}>
          <StatusBanner run={run} onReview={onReview} onAction={onAction} />
          <ProgramsFundingDetails
            run={run}
            onOpenSettings={() => navigate('/settings/funding')}
          />
          <SupportingDocuments
            serviceUrl={serviceUrl ?? ''}
            accountId={accountId ?? ''}
            program={program ?? ''}
            workflowId={workflowId ?? ''}
            displayStatus={run.display_status}
            attachments={run.detail_fields?.attachments}
            onUploaded={onDocumentUploaded}
          />
          <ContactInformation contact={run.detail_fields?.contact} />
        </div>
        <div className={classes.rightColumn}>
          <FundRequestProgress progress={run.funding_progress} />
          <RecentActivities events={events} onReview={onReview} />
        </div>
      </div>
    </div>
  )
}

export default FundingRequestDetail
